import { dateFormatter } from "../utilities/common";

const baseUrl = '/api'

let viewAsHouseId = null;

export function setViewAsHouseId(id) {
    viewAsHouseId = id;
}

function apiFetch(path, opts = {}) {
    if (!viewAsHouseId) {
        return fetch(path, opts);
    }
    const sep = path.includes('?') ? '&' : '?';
    return fetch(`${path}${sep}as_house=${encodeURIComponent(viewAsHouseId)}`, opts);
}

export async function listAdminHouses() {
    const response = await apiFetch(`${baseUrl}/admin/houses`);
    if (!response.ok) {
        throw new Error('Failed to load houses');
    }
    const body = await response.json();
    return body.houses || [];
}

export async function getAdminStats() {
    const response = await apiFetch(`${baseUrl}/admin/stats`);
    if (!response.ok) {
        throw new Error('Failed to load admin stats');
    }
    return await response.json();
}

export async function getAdminTrends(months = 12) {
    const response = await apiFetch(`${baseUrl}/admin/stats/trends?months=${months}`);
    if (!response.ok) {
        throw new Error('Failed to load admin trends');
    }
    return await response.json();
}

// The three admin drill-in lists share the same window contract: from/to as
// YYYY-MM-DD (to-day inclusive), defaulting server-side to the last month.
function windowQuery({ from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export async function getAdminGames(window) {
    const response = await apiFetch(`${baseUrl}/admin/games${windowQuery(window)}`);
    if (!response.ok) {
        throw new Error('Failed to load games');
    }
    return await response.json();
}

export async function getAdminNewAccounts(window) {
    const response = await apiFetch(`${baseUrl}/admin/accounts${windowQuery(window)}`);
    if (!response.ok) {
        throw new Error('Failed to load new accounts');
    }
    return await response.json();
}

export async function getAdminActiveHosts(window) {
    const response = await apiFetch(`${baseUrl}/admin/hosts${windowQuery(window)}`);
    if (!response.ok) {
        throw new Error('Failed to load active hosts');
    }
    return await response.json();
}

export async function listEmailTemplates() {
    const response = await apiFetch(`${baseUrl}/admin/email/templates`);
    if (!response.ok) {
        throw new Error('Failed to load email templates');
    }
    const body = await response.json();
    return body.templates || [];
}

export async function getEmailRecipients(segment = '') {
    const query = segment ? `?segment=${encodeURIComponent(segment)}` : '';
    const response = await apiFetch(`${baseUrl}/admin/email/recipients${query}`);
    if (!response.ok) {
        throw new Error('Failed to load recipients');
    }
    const body = await response.json();
    return body.recipients || [];
}

export async function previewEmails({ templateId, recipients }) {
    const response = await apiFetch(`${baseUrl}/admin/email/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, recipients }),
    });
    if (!response.ok) {
        throw new Error('Failed to render preview');
    }
    const body = await response.json();
    return body.previews || [];
}

export async function sendEmails({ templateId, recipients, from, fromName, replyTo }) {
    const response = await apiFetch(`${baseUrl}/admin/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, recipients, from, fromName, replyTo }),
    });
    if (!response.ok) {
        throw new Error('Failed to send campaign');
    }
    return await response.json();
}

// submitSurvey posts a campaign survey response. Public (no auth) — recipients
// arrive from an email link. email is the attribution token from the link's ?e=.
export async function submitSurvey({ survey, email, rating, comment }) {
    const response = await fetch(`${baseUrl}/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey, email, rating, comment }),
    });
    if (!response.ok) {
        throw new Error('Failed to submit survey');
    }
    return await response.json().catch(() => ({}));
}

export async function listSurveyResponses(survey = '') {
    const query = survey ? `?survey=${encodeURIComponent(survey)}` : '';
    const response = await apiFetch(`${baseUrl}/admin/surveys${query}`);
    if (!response.ok) {
        throw new Error('Failed to load survey responses');
    }
    const body = await response.json();
    return body.responses || [];
}

export async function getSystemStatus() {
    const response = await apiFetch('/status');
    if (!response.ok) {
        throw new Error('Failed to load system status');
    }
    return await response.json();
}

export async function getMe() {
    const response = await apiFetch(`${baseUrl}/me`);
    if (response.status === 401) {
        return null;
    }
    if (!response.ok) {
        throw new Error('Failed to load session info');
    }
    return await response.json();
}

// updateProfile saves the user's first/last name (US-004). Returns the updated
// profile shape ({ user: {...} }, same as getMe).
export async function updateProfile({ firstName, lastName }) {
    const response = await apiFetch(`${baseUrl}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || 'Failed to update your name');
    }
    return body;
}

// renameHouse renames the user's owned house (US-005).
export async function renameHouse({ name }) {
    const response = await apiFetch(`${baseUrl}/house`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || 'Failed to rename your house');
    }
    return body;
}

// changePassword changes a basic-auth user's password (US-006).
export async function changePassword({ currentPassword, newPassword }) {
    const response = await apiFetch(`${baseUrl}/profile/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || 'Failed to change your password');
    }
    return body;
}

// updateEmailPreferences toggles the marketing-email opt-in (US-007).
export async function updateEmailPreferences({ marketingEmailsEnabled }) {
    const response = await apiFetch(`${baseUrl}/profile/email-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketingEmailsEnabled }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || 'Failed to update email preferences');
    }
    return body;
}

export async function startImpersonation(userId, reason) {
    const response = await apiFetch(`${baseUrl}/admin/impersonate/${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || 'Failed to start impersonation');
    }
    return body;
}

export async function stopImpersonation() {
    const response = await apiFetch(`${baseUrl}/admin/impersonate/stop`, {
        method: 'POST',
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to stop impersonation');
    }
    return await response.json();
}

export async function login({ email, password }) {
    // Post to /api/login (routed to the API via the CloudFront /api/* behavior)
    // so the bare /login path can be served as the static SPA login page.
    const response = await fetch('/api/login', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
    });

    const user = await response.json();
    if (!response.ok) {
        throw new Error('Invalid login attempt')
    }

    return user;
}

export async function logout() {
    const response = await fetch("/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
        throw new Error('Logout failed');
    }
}

// Public: request a password-reset link. Always resolves for any email (the API
// returns a generic 200 to avoid revealing whether an account exists).
export async function requestPasswordReset({ email }) {
    const response = await fetch(`${baseUrl}/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    });
    if (!response.ok) {
        throw new Error("Something went wrong. Please try again.");
    }
    return await response.json().catch(() => ({}));
}

// Public: complete a password reset with a token from the emailed link.
export async function confirmPasswordReset({ token, newPassword }) {
    const response = await fetch(`${baseUrl}/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || body.message || "This reset link is invalid or has expired.");
    }
    return body;
}

// sendVerificationEmail (authenticated) re-sends the current user's verification
// email. The cookie carries the identity, so no body is needed.
export async function sendVerificationEmail() {
    const response = await apiFetch(`${baseUrl}/profile/verify-email/send`, {
        method: "POST",
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || body.message || "Couldn't send the verification email.");
    }
    return body;
}

// confirmEmailVerification (public) verifies the token from the emailed link.
export async function confirmEmailVerification({ token }) {
    const response = await fetch(`${baseUrl}/verify-email/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || body.message || "This verification link is invalid or has expired.");
    }
    return body;
}

export async function getHouse() {
    let body = {}
    const response = await apiFetch(`${baseUrl}/houses`);
    body = await response.json();
    if (response.status !== 200) {
        throw new Error("Unable to fetch house")
    }
   return body
}

export async function getPlayerSummaryStats() {
    const response = await apiFetch(`${baseUrl}/players/summary`);
    if (!response.ok) {
        throw new Error('Failed to load player summary stats');
    }
    return await response.json();
}

export async function createGame({ description, date }) {
    let game = {}

    if (!description || description === '') {
        throw new Error('You must provide a description for your event')
    }

    const parts = date.split('-');
    const myDate = new Date(parts[0], parts[1] - 1, parts[2]);

    if (isNaN(myDate)) {
        throw new Error('The date provided is invalid')
    }

    const formattedDate = dateFormatter(myDate)

    const response = await apiFetch(`${baseUrl}/games`, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            description: description,
            date: formattedDate
        })
    })
    if (response.status !== 201) {
        throw new Error("game could not be created")
    }
    game = await response.json();

    return game
}

export async function addMember({ name }) {
    if (!name || name === '') {
        throw new Error('You must provide a name')
    }

    let member = {}
    const response = await apiFetch(`${baseUrl}/members`, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            name: name,
        })
    })
    if (response.status !== 201) {
        if (response.status === 409) {
            throw new Error(`Member ${name} already exists`)
        }
        throw new Error("Invalid member response")
    }
    member = await response.json();
    return member
}

export async function updateMember(memberId, name) {
    if (!memberId || memberId === '') {
        throw new Error('You must provide a member id')
    }
    if (!name || name === '') {
        throw new Error('You must provide a name')
    }

    const response = await apiFetch(`${baseUrl}/members/${memberId}`, {
        method: "PATCH",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            name: name,
        })
    })
    if (!response.ok) {
        throw new Error("Invalid member response")
    }

    return await response.json()
}


export async function fetchGame({ gameId }) {
    let game = {};

    try {
        const response = await apiFetch(`${baseUrl}/games/${gameId}`)
        game = await response.json();
        if (!response.ok) {
            game = null
        }
    } catch (e) {
        throw e;
    }
    return game
}

// The table cash-out plan: who pays whom (minimized), plus any discrepancy.
export async function fetchSettlement({ gameId }) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/settlement`);
    const body = await response.json();
    if (!response.ok) {
        throw new Error(body.error ? body.error : "unable to load settlement");
    }
    return body;
}

// Close the books: settle every outstanding transaction in the game. POST to
// the settlement resource (GET on the same path returns the plan).
export async function settleGame({ gameId }) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/settlement`, {
        method: "POST",
        cache: "no-cache",
    });
    if (!response.ok) {
        let message = "unable to settle the game";
        try {
            const body = await response.json();
            if (body.error) message = body.error;
        } catch (e) {
            // no JSON body
        }
        throw new Error(message);
    }
    return true;
}

export async function addPlayer({ gameId, memberId }) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/players`, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            playerId: memberId
        })
    });

    const body = await response.json()

    if (!response.ok) {
        const message = body.error ? body.error : "add player failed"
        throw new Error(message)
    }

    return body
}

export async function removePlayer({ gameId, playerId }) {

    const response = await apiFetch(`${baseUrl}/games/${gameId}/players/${playerId}`, {
        method: "DELETE",
        cache: "no-cache",
    });

    if (!response.ok) {
        const body = await response.json()
        const message = body.error ? body.error : "remove player failed"
        throw new Error(message)
    }
}

export async function createTransaction({gameId, playerId, transaction}) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/players/${playerId}/transactions`, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(transaction)
    });

    const body = await response.json()

    if (!response.ok) {
        const message = body.error ? body.error : "create transaction failed"
        throw new Error(message)
    }

    return body

}

// Guided cash-out: one request that creates the covers, settles fully-covered
// buy-ins, and records the payouts server-side.
export async function cashOutPlayer({gameId, playerId, covers, payouts}) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/players/${playerId}/cash-out`, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ covers, payouts })
    });

    const body = await response.json()

    if (!response.ok) {
        const message = body.error ? body.error : "cash out failed"
        throw new Error(message)
    }

    return body
}

export async function updateTransaction({gameId, transaction}) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/transactions/${transaction.id}`, {
        method: "PUT",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(transaction)
    });

    const body = await response.json()

    if (!response.ok) {
        const message = body.error ? body.error : "update transaction failed"
        throw new Error(message)
    }

    return body
}

export async function deleteTransaction(gameId, transactionId) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}/transactions/${transactionId}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error('Failed to delete transaction');
    }
}

export async function deleteGame(gameId) {
    const response = await apiFetch(`${baseUrl}/games/${gameId}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error('Failed to delete transaction');
    }
}

export async function postFeedback({ name, feedback }) {
    const response = await apiFetch(`${baseUrl}/feedback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: name,
            feedback: feedback
        })
    });

    if (!response.ok) {
        throw new Error('Failed to post feedback');
    }
}

export async function getTermsStatus() {
    const response = await fetch(`${baseUrl}/me/terms`);
    if (!response.ok) {
        throw new Error('Failed to load terms status');
    }
    return await response.json();
}

export async function acceptTerms() {
    const response = await fetch(`${baseUrl}/me/terms/accept`, {
        method: 'POST',
    });
    if (!response.ok) {
        throw new Error('Failed to record terms acceptance');
    }
    return await response.json();
}
